import React, { useState } from "react";
import { Link, Form, router } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChevronDown, ChevronUp, Trash, X } from "lucide-react";

interface LineItem {
  description: string;
  hours: string;
  price_per_hour: string;
}

interface InvoiceFormData {
  issue_date: string;
  due_date: string;
  subject: string;
  from_company: string;
  invoice_for: string;
  client_id: string;
  status: string;
  line_items_attributes: LineItem[];
}

interface UserSettings {
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  tax_id?: string;
}

interface Client {
  id: number;
  company_name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  hourly_rate?: number;
}

interface Props {
  invoice: {
    issue_date: string;
    due_date: string;
    from_company?: string;
  };
  clients?: Client[];
  user_settings?: UserSettings;
  errors?: Record<string, string>;
}

export default function New({ invoice, clients = [], user_settings, errors = {} }: Props) {
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [data, setData] = useState<InvoiceFormData>({
    issue_date: invoice.issue_date || new Date().toISOString().split("T")[0],
    due_date:
      invoice.due_date ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
    subject: "",
    from_company: invoice.from_company || user_settings?.company_name || "",
    invoice_for: "",
    client_id: "",
    status: "draft",
    line_items_attributes: [{ description: "", hours: "", price_per_hour: "" }],
  });

  const updateData = (field: keyof InvoiceFormData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleClientChange = (clientId: string) => {
    updateData("client_id", clientId);
    const selectedClient = clients.find((c) => c.id.toString() === clientId);
    if (selectedClient) {
      updateData("invoice_for", selectedClient.company_name);

      // If there's a default hourly rate and the first line item has no rate, set it
      if (selectedClient.hourly_rate && data.line_items_attributes.length > 0) {
        const updatedLineItems = [...data.line_items_attributes];
        if (!updatedLineItems[0].price_per_hour) {
          updatedLineItems[0].price_per_hour =
            selectedClient.hourly_rate.toString();
          updateData("line_items_attributes", updatedLineItems);
        }
      }
    } else {
      updateData("invoice_for", "");
    }
  };

  const addLineItem = () => {
    const selectedClient = clients.find(
      (c) => c.id.toString() === data.client_id,
    );
    const defaultRate = selectedClient?.hourly_rate?.toString() || "";

    updateData("line_items_attributes", [
      ...data.line_items_attributes,
      { description: "", hours: "", price_per_hour: defaultRate },
    ]);
  };

  const removeLineItem = (index: number) => {
    const newLineItems = data.line_items_attributes.filter(
      (_, i) => i !== index,
    );
    updateData("line_items_attributes", newLineItems);
  };

  const updateLineItem = (
    index: number,
    field: keyof LineItem,
    value: string,
  ) => {
    const newLineItems = [...data.line_items_attributes];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    updateData("line_items_attributes", newLineItems);
  };

  const calculateTotal = () => {
    return data.line_items_attributes.reduce((sum, item) => {
      const hours = parseFloat(item.hours) || 0;
      const pricePerHour = parseFloat(item.price_per_hour) || 0;
      return sum + hours * pricePerHour;
    }, 0);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">
        Create New Invoice
      </h1>

      <Form action="/invoices" className="space-y-8" method="post">
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label
                  htmlFor="issue_date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Issue Date
                </label>
                <input
                  type="date"
                  id="issue_date"
                  name="issue_date"
                  defaultValue={data.issue_date}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.issue_date && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.issue_date}
                  </span>
                )}
              </div>

              <div>
                <label
                  htmlFor="due_date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Due Date
                </label>
                <input
                  type="date"
                  id="due_date"
                  name="due_date"
                  defaultValue={data.due_date}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.due_date && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.due_date}
                  </span>
                )}
              </div>

              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  defaultValue={data.status}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
                {errors.status && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.status}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label
                htmlFor="client_id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Select Client
              </label>
              <select
                id="client_id"
                name="client_id"
                value={data.client_id}
                onChange={(e) => handleClientChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">
                  Select a client or enter manually below
                </option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>
              {clients.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  <Link
                    href="/clients/new"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Add your first client
                  </Link>{" "}
                  to use the dropdown
                </p>
              )}
              {data.client_id && (() => {
                const selectedClient = clients.find(c => c.id.toString() === data.client_id);
                return selectedClient?.hourly_rate && (
                  <p className="text-sm text-blue-600 mt-1">
                    Default hourly rate: ${parseFloat(selectedClient.hourly_rate.toString()).toFixed(2)}/hour
                  </p>
                );
              })()}
            </div>

            <div className="mt-6 border-t pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAdditionalFields(!showAdditionalFields)}
                className="flex items-center gap-2 text-sm"
              >
                {showAdditionalFields ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Hide Additional Fields
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Show Additional Fields (Subject, From, Invoice For)
                  </>
                )}
              </Button>

              {showAdditionalFields && (
                <div className="mt-4 space-y-6">
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Subject (Optional)
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      defaultValue={data.subject}
                      placeholder="e.g., Website Development - Phase 1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.subject && (
                      <span className="text-red-500 text-sm mt-1">
                        {errors.subject}
                      </span>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="from_company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      From (Company Name)
                    </label>
                    <input
                      type="text"
                      id="from_company"
                      name="from_company"
                      defaultValue={data.from_company}
                      required
                      placeholder="Your Company Name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {!user_settings?.company_name && (
                      <p className="text-sm text-gray-500 mt-1">
                        Tip:{" "}
                        <Link
                          href="/settings/edit"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Configure your company settings
                        </Link>{" "}
                        to auto-fill this field
                      </p>
                    )}
                    {errors.from_company && (
                      <span className="text-red-500 text-sm mt-1">
                        {errors.from_company}
                      </span>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="invoice_for"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Invoice For (Company Name)
                    </label>
                    <input
                      type="text"
                      id="invoice_for"
                      name="invoice_for"
                      value={data.invoice_for}
                      onChange={(e) => updateData("invoice_for", e.target.value)}
                      required
                      placeholder="Client Name or Company"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.invoice_for && (
                      <span className="text-red-500 text-sm mt-1">
                        {errors.invoice_for}
                      </span>
                    )}
                    {data.client_id && (
                      <p className="text-sm text-gray-500 mt-1">
                        This field was auto-filled from the selected client
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Line Items</CardTitle>
              <Button
                type="button"
                onClick={addLineItem}
                variant="secondary"
                size="sm"
              >
                Add Line Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700 pb-2 border-b">
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Hours</div>
                <div className="col-span-2">Price/Hour</div>
                <div className="col-span-2">Total</div>
                <div className="col-span-1"></div>
              </div>

              {data.line_items_attributes.map((item, index) => {
                const itemTotal =
                  (parseFloat(item.hours) || 0) *
                  (parseFloat(item.price_per_hour) || 0);
                return (
                  <div key={index} className="grid grid-cols-12 gap-4 py-2">
                    <input
                      type="text"
                      name={`line_items_attributes[${index}][description]`}
                      value={item.description}
                      onChange={(e) =>
                        updateLineItem(index, "description", e.target.value)
                      }
                      placeholder="Description"
                      required
                      className="col-span-5 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      step="0.01"
                      name={`line_items_attributes[${index}][hours]`}
                      value={item.hours}
                      onChange={(e) =>
                        updateLineItem(index, "hours", e.target.value)
                      }
                      placeholder="0.00"
                      required
                      className="col-span-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      step="0.01"
                      name={`line_items_attributes[${index}][price_per_hour]`}
                      value={item.price_per_hour}
                      onChange={(e) =>
                        updateLineItem(index, "price_per_hour", e.target.value)
                      }
                      placeholder="0.00"
                      required
                      className="col-span-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="col-span-2 px-3 py-2 text-gray-900 font-medium">
                      ${itemTotal.toFixed(2)}
                    </div>
                    <div className="col-span-1">
                      {data.line_items_attributes.length > 1 && (
                        <Button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          variant="destructive"
                          size="sm"
                          className="w-full"
                        >
                          <span className="sr-only">Remove</span>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end">
              <div className="text-lg font-semibold text-gray-900">
                Amount Due:{" "}
                <span className="text-2xl">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            onClick={() => router.visit("/invoices")}
            variant="outline"
          >
            Cancel
          </Button>
          <Button type="submit">
            Create Invoice
          </Button>
        </div>
      </Form>
    </div>
  );
}
